import React, { useEffect, useRef, useState } from "react";
import { Upload, Image as ImageIcon, Video, Trash2, Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminListMedia, adminUpload, adminDeleteMedia, formatApiError } from "../../lib/api";

const API_ORIGIN = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && u.startsWith("/api/uploads/")) ? `${API_ORIGIN}${u}` : u;

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [copied, setCopied] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const items = await adminListMedia();
      setItems(items);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const res = await adminUpload(f);
        setItems((xs) => [res, ...xs]);
      }
      toast.success(`Uploaded ${files.length} file${files.length > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try {
      await adminDeleteMedia(id);
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(url);
      setTimeout(() => setCopied(""), 1500);
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.kind === filter);

  return (
    <div data-testid="admin-media-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-10">
        <div>
          <p className="kicker text-stone-500">Dashboard</p>
          <h1 className="font-serif text-4xl font-light mt-2 text-stone-900">Media library</h1>
          <p className="text-stone-600 text-sm mt-1">{items.length} files · images &amp; videos</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}
                  data-testid="media-filter-select"
                  className="bg-white border border-stone-300 px-3 py-2 text-sm">
            <option value="all">All</option>
            <option value="image">Images</option>
            <option value="video">Videos</option>
          </select>
          <button onClick={() => fileRef.current?.click()}
                  data-testid="media-upload-button"
                  disabled={uploading}
                  className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 text-[12px] tracking-[0.18em] uppercase font-semibold hover:bg-[#9c4122] transition-colors disabled:opacity-50">
            {uploading ? <><Loader2 className="animate-spin" size={14} /> Uploading</> : <><Upload size={14} /> Upload</>}
          </button>
          <input ref={fileRef} type="file" multiple accept="image/*,video/*" className="hidden"
                 data-testid="media-file-input"
                 onChange={(e) => handleFiles(e.target.files)} />
        </div>
      </div>

      {loading ? (
        <p className="text-stone-500 text-sm">Loading…</p>
      ) : filtered.length === 0 ? (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
          data-testid="media-dropzone"
          className="border border-dashed border-stone-300 py-24 text-center text-stone-500"
        >
          <Upload size={24} className="mx-auto mb-3" />
          <p className="font-serif text-2xl text-stone-700">Drop files here or click Upload above.</p>
          <p className="mt-2 text-xs">jpg, png, webp, gif · mp4, mov, webm · up to 100 MB</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((m) => (
            <div key={m.id} data-testid={`media-item-${m.id}`} className="group bg-white border border-stone-200">
              <div className="aspect-video bg-stone-100 overflow-hidden relative flex items-center justify-center">
                {m.kind === "image" ? (
                  <img src={absUrl(m.url)} alt={m.original_name} loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-stone-500">
                    <Video size={28} />
                    <span className="text-xs">{m.original_name}</span>
                  </div>
                )}
                <span className="absolute top-2 left-2 text-[9px] tracking-widest uppercase font-semibold bg-stone-900 text-white px-2 py-1">{m.kind}</span>
              </div>
              <div className="p-3">
                <p className="text-xs text-stone-700 truncate font-medium">{m.original_name}</p>
                <p className="text-[10px] text-stone-500 mt-0.5">{(m.size / 1024).toFixed(0)} KB</p>
                <div className="flex items-center gap-1 mt-2">
                  <button onClick={() => copy(absUrl(m.url))}
                          data-testid={`media-copy-${m.id}`}
                          className="flex-1 text-[10px] tracking-widest uppercase border border-stone-300 px-2 py-1 hover:border-stone-900 inline-flex items-center justify-center gap-1">
                    {copied === absUrl(m.url) ? <><Check size={11} /> Copied</> : <><Copy size={11} /> URL</>}
                  </button>
                  <button onClick={() => onDelete(m.id, m.original_name)}
                          data-testid={`media-delete-${m.id}`}
                          className="text-[10px] px-2 py-1 border border-stone-300 text-red-600 hover:border-red-600">
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
